import type { ColumnDef } from "@tanstack/react-table"
import { useMemo } from "react"

import { PayFileInvoiceRowActions } from "../components/PayFileInvoiceRowActions"

import StyledChip from "@/components/common/Chip/Chip"
import type { PayFileListFieldsFragment } from "@/graphql/codegen/graphql"
import { InvoicesDatePaidCell } from "@/screens/Accounting/subfeatures/Invoices/components/InvoicesDatePaidCell"
import {
  INVOICE_PAYMENT_METHOD_LABELS,
  INVOICE_STATUS_CHIP_CLASSES,
  INVOICE_STATUS_LABELS,
  INVOICE_TYPE_LABELS,
} from "@/screens/Accounting/subfeatures/Invoices/constants/labels"
import { formatCurrencyWithCents } from "@/utils/currency"
import { formatDate } from "@/utils/date"

export type PayFileInvoiceLineFields = PayFileListFieldsFragment["invoiceLines"][number]

type UsePayFileInvoiceColumnsReturn = ColumnDef<PayFileInvoiceLineFields>[]

interface UsePayFileInvoiceColumnsParams {
  payFileId: string
  canRemove: boolean
  onSuccess: () => void
}

export const usePayFileInvoiceColumns = ({
  payFileId,
  canRemove,
  onSuccess,
}: UsePayFileInvoiceColumnsParams): UsePayFileInvoiceColumnsReturn =>
  useMemo<ColumnDef<PayFileInvoiceLineFields>[]>(
    () => [
      {
        id: "invoiceNumber",
        header: "Vendor Invoice #",
        cell: ({ row }) => row.original.invoice.invoiceNumber,
      },
      {
        id: "vendor",
        header: "Vendor",
        cell: ({ row }) => row.original.invoice.vendorCompany?.name ?? null,
      },
      {
        id: "type",
        header: "Type",
        cell: ({ row }) => (
          <StyledChip
            label={INVOICE_TYPE_LABELS[row.original.invoice.invoiceType]}
            className='bg-primary-200 text-gray-900'
            size='small'
          />
        ),
      },
      {
        id: "dateEntered",
        header: "Date Entered",
        cell: ({ row }) => (row.original.invoice.createdOn ? formatDate(row.original.invoice.createdOn) : null),
      },
      {
        id: "dueDate",
        header: "Due Date",
        cell: ({ row }) => formatDate(row.original.invoice.dueDate),
      },
      {
        id: "scheduledPayDate",
        header: "Scheduled Pay Date",
        cell: ({ row }) => formatDate(row.original.invoice.scheduledPayDate),
      },
      {
        id: "paymentType",
        header: "Payment Type",
        cell: ({ row }) => INVOICE_PAYMENT_METHOD_LABELS[row.original.invoice.paymentMethod],
      },
      {
        id: "amount",
        header: "Amount",
        cell: ({ row }) => <span className='tabular-nums'>{formatCurrencyWithCents(row.original.amount)}</span>,
        meta: { align: "right" },
      },
      {
        id: "printedCheck",
        header: "Printed Check",
        cell: ({ row }) => (row.original.invoice.printedCheck ? "Yes" : "No"),
      },
      {
        id: "status",
        header: "Status",
        cell: ({ row }) => (
          <StyledChip
            label={INVOICE_STATUS_LABELS[row.original.invoice.status]}
            className={INVOICE_STATUS_CHIP_CLASSES[row.original.invoice.status]}
            size='small'
          />
        ),
      },
      {
        id: "datePaid",
        header: "Date Paid",
        cell: ({ row }) => <InvoicesDatePaidCell invoice={row.original.invoice} />,
      },
      {
        id: "actions",
        header: "",
        size: 112,
        enableSorting: false,
        cell: ({ row }) => (
          <PayFileInvoiceRowActions row={row} payFileId={payFileId} canRemove={canRemove} onSuccess={onSuccess} />
        ),
      },
    ],
    [payFileId, canRemove, onSuccess]
  )
